<!--@format-->
<template>
    <div class="contact-edit-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="currentStep.loading" class="loading-card">
                    <loading-screen-svg />
                </div>
                <div v-else-if="currentStep.title === 'Edit Contact'" class="step">
                    <div class="bordered-container">
                        <div class="title">Details</div>
                        <div class="company-form-group">
                            <div class="contact-form-row contact-profile-image">
                                <image-upload
                                    class="test"
                                    id="image-upload"
                                    :uploadUrl="uploadUrl"
                                    :placeholderImage="placeholderImage"
                                    :providedImageUrl="contact.profileImageUrl"
                                    @fileUploadSuccess="updateImageUrl"
                                    orientation="horizontal"
                                />
                            </div>

                            <div class="contact-form-row">
                                <div class="input-container">
                                    <app-input v-model="contact.firstName" :label="'First Name'" />
                                </div>
                                <div class="input-container">
                                    <app-input v-model="contact.lastName" :label="'Last Name'" />
                                </div>
                            </div>
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <app-input v-model="contact.email" :label="'Email'" />
                                </div>
                                <div class="input-container">
                                    <label>Company</label>
                                    <company-search
                                        v-model="contact.company"
                                        :allowManage="true"
                                        :clientId="contact.clientId"
                                    />
                                </div>
                            </div>

                            <div class="contact-form-row">
                                <div class="input-container">
                                    <app-input v-model="contact.title" :label="'Title'" />
                                </div>
                                <div class="input-container">
                                    <app-input
                                        v-model="contact.initialLeadScore"
                                        :label="'Initial Lead Score'"
                                        type="number"
                                    />
                                </div>
                            </div>
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <label>Status</label>
                                    <contact-status-dropdown
                                        v-model="contact.contactStatus"
                                        :clientId="contact.clientId"
                                    />
                                </div>
                            </div>
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <label>Assigned Salesperson</label>
                                    <salesperson-search
                                        v-model="contact.salesperson"
                                        :clientId="contact.clientId"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bordered-container">
                        <div class="title long-title">About this Contact</div>
                        <div class="company-form-group">
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <app-input v-model="contact.phone" :label="'Work Phone'" />
                                </div>
                                <div class="input-container">
                                    <app-input
                                        v-model="contact.mobilePhone"
                                        :label="'Mobile Phone'"
                                    />
                                </div>
                            </div>
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <app-input
                                        v-model="contact.teamsPhone"
                                        :label="'Teams Phone'"
                                    />
                                </div>
                                <div class="input-container">
                                    <app-input v-model="contact.homePhone" :label="'Home Phone'" />
                                </div>
                            </div>
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <app-input
                                        v-model="contact.addressLine1"
                                        :label="'Address Line 1'"
                                    />
                                </div>
                                <div class="input-container">
                                    <app-input
                                        v-model="contact.addressLine2"
                                        :label="'Address Line 2'"
                                    />
                                </div>
                            </div>
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <app-input v-model="contact.city" :label="'City'" />
                                </div>
                                <div class="input-container">
                                    <app-input v-model="contact.region" :label="'State'" />
                                </div>
                            </div>
                            <div class="contact-form-row">
                                <div class="input-container zip-code-container">
                                    <app-input v-model="contact.zipCode" :label="'Zip Code'" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bordered-container">
                        <div class="title">Social Media</div>
                        <div class="company-form-group">
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <app-input
                                        v-model="contact.linkedinUrl"
                                        :label="'LinkedIn URL'"
                                    />
                                </div>
                            </div>
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <app-input
                                        v-model="contact.facebookUrl"
                                        :label="'Facebook URL'"
                                    />
                                </div>
                            </div>
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <app-input
                                        v-model="contact.twitterUrl"
                                        :label="'X (Formerly Twitter) URL'"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bordered-container">
                        <div class="title long-title">Additional Information</div>
                        <div class="company-form-group">
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <div class="info">
                                        <profile-lists-dropdown
                                            :contact="contact"
                                            @input="addList($event)"
                                            @remove="removeList($event)"
                                            :contactId="contact.id"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <label>Subscription</label>
                                    <app-input-radio
                                        v-model="contact.isSubscribed"
                                        :options="subscribeOptions"
                                    />
                                </div>
                                <div class="input-container">
                                    <label>Buyer Role</label>
                                    <app-input-radio
                                        v-model="contact.buyerRole"
                                        :options="buyerRoleOptions"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bordered-container">
                        <div class="title">Bounced Status</div>
                        <div class="company-form-group">
                            <div class="contact-form-row">
                                <div class="input-container">
                                    <label>Is Bounced</label>
                                    <app-input-radio
                                        v-model="contact.isBounced"
                                        :options="bouncedOptions"
                                    />
                                </div>
                            </div>
                            <div>
                                <p>
                                    This value is automatically changed when the email has bounced.
                                    The email address can no longer be sent emails, regardless of
                                    changing the value. However, if the email address has been
                                    fixed, please reach out to support to have it removed from the
                                    bounce list.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="bordered-container">
                        <div class="title">Custom Fields</div>
                        <div class="company-form-group">
                            <div
                                v-for="element in customContactFields"
                                :key="element.id"
                                class="contact-form-row"
                            >
                                <div class="input-container">
                                    <custom-field-design-element :element="element" />
                                </div>
                            </div>
                            <app-button class="add" @click="openCustomFieldMenu">
                                Add a Custom Field
                                <o6-add-svg />
                            </app-button>
                            <p class="disclaimer">
                                To manage your custom fields, go to your
                                <router-link
                                    @click.native="closeWidget"
                                    :to="`${routeBaseUrl}/Account/Customization/CustomContactFields`"
                                    >account settings</router-link
                                >.
                            </p>
                        </div>
                    </div>
                </div>
                <div v-else-if="currentStep.title === 'Saving'" class="step scrubbing-step">
                    <quick-add-contact-svg />
                </div>
            </div>

            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="currentStep.action" :loading="currentStep.loading">
                        {{ currentStep.buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const AppInputRadio = () => import('@/components/common/app-input-radio.vue');
const ContactStatusDropdown = () => import('@/components/contact/contact-status-dropdown');
const SalespersonSearch = () => import('@/components/user/salesperson-search');
const QuickAddScrubbingSvg = () => import('@/components/svg/quick-add/quick-add-scrubbing-svg.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const Multiselect = () => import('vue-multiselect');
const ImageUpload = () => import('@/components/common/image-upload.vue');
const CompanySearch = () => import('@/components/company/company-search');
const ListSearch = () => import('@/components/list/list-search');
const XSvg = () => import('@/components/svg/connectwise/x-svg');
const CustomFieldDesignElement = () =>
    import('@/components/custom-field/custom-field-design-element');
const AppBorderSection = () => import('@/components/common/app-border-section');
const O6AddSvg = () => import('@/components/svg/grid/o6-add-svg');
const ProfileListsDropdown = () => import('@/components/profile/profile-lists-dropdown');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import AppPopup from '@/components/popups/app-popup';
import { CustomFieldsServiceMixin } from '@/mixins/custom-fields-service-mixin';
import log, { LogLevel } from '@/scripts/logger';
import { v4 as uuidv4 } from 'uuid';

export default {
    name: 'contact-edit-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'app-input-radio': AppInputRadio,
        'quick-add-contact-svg': QuickAddScrubbingSvg,
        multiselect: Multiselect,
        'image-upload': ImageUpload,
        'widget-close-svg': WidgetCloseSvg,
        'contact-status-dropdown': ContactStatusDropdown,
        'company-search': CompanySearch,
        'list-search': ListSearch,
        'salesperson-search': SalespersonSearch,
        'x-svg': XSvg,
        'custom-field-design-element': CustomFieldDesignElement,
        'app-border-section': AppBorderSection,
        'o6-add-svg': O6AddSvg,
        'profile-lists-dropdown': ProfileListsDropdown,
        'loading-screen-svg': LoadingScreenSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    mixins: [CustomFieldsServiceMixin],

    data() {
        return {
            isInitializing: true,
            placeholderImage: require('@/assets/images/avatar.png'),
            uploadUrl: '/api/Files',

            subscribeOptions: [
                {
                    name: 'Subscribed',
                    value: 'subscribed',
                },
                {
                    name: 'Unsubscribed',
                    value: 'unsubscribed',
                },
            ],

            buyerRoleOptions: [
                {
                    name: 'Decision Maker',
                    value: 2,
                },
                {
                    name: 'Advocate',
                    value: 1,
                },
                {
                    name: 'None',
                    value: 0,
                },
            ],

            bouncedOptions: [
                {
                    name: 'Yes',
                    value: 'yes',
                },
                {
                    name: 'No',
                    value: 'no',
                },
            ],

            contact: {
                id: null,
                initialIsSubscribed: null,
                initialEmail: null,
                isSubscribed: null,
                isBounced: null,
                buyerRole: 0,
                clientId: null,
                email: null,
                firstName: null,
                lastName: null,
                title: null,
                city: null,
                region: null,
                profileImageUrl: null,
                initialLeadScore: 0,
                linkedinUrl: null,
                facebookUrl: null,
                twitterUrl: null,
                phone: null,
                mobilePhone: null,
                teamsPhone: null,
                homePhone: null,
                contactStatus: null,
                company: null,
                salesperson: null,
                lists: [],
                addressLine1: null,
                addressLine2: null,
                zipCode: null,
            },

            currentStep: null,

            steps: {
                contact: {
                    title: 'Edit Contact',
                    buttonText: 'Save',
                    loading: false,
                    action: this.saveContact,
                },
                saving: {
                    title: 'Saving...',
                    buttonText: 'Saving',
                    loading: true,
                    action: () => {},
                },
                loading: {
                    title: 'Loading...',
                    buttonText: 'Loading...',
                    loading: true,
                    action: () => {},
                },
            },

            customContactFields: [],
        };
    },

    computed: {
        customFieldsConfig() {
            return { type: 'Contact', clientId: this.contact.clientId, contactId: this.contact.id };
        },

        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },
    },

    created() {
        this.contact.id = this.widget.options.contactId;
        this.goToStep('loading');

        this.getContact().then(() => {
            this.getCustomContactFields()
                .then(() => {
                    this.goToStep('contact');
                })
                .catch((error) => {
                    this.closeWidget();
                });
        });

        EventBus.$on('custom-field:update', this.getCustomContactFields);
    },

    beforeDestroy() {
        EventBus.$off('custom-field:update', this.getCustomContactFields);
    },

    methods: {
        addList(newList) {
            var exists = this.contact.lists.some((list) => {
                return list.id === newList.id;
            });

            if (!exists) {
                newList.class = 'solid-grey-bg';
                this.contact.lists.push(newList);
            }
        },

        removeList(list) {
            this.contact.lists.splice(this.contact.lists.indexOf(list), 1);
        },

        showSubscriptionWarning() {
            var actions = {
                confirm: {
                    label: 'Ok',
                },
            };

            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Warning',
                    content: `<p>You have resubscribed a contact without their explicit permission. Please refer to legislation <a style="color: #18AEFF" href="https://www.ftc.gov/tips-advice/business-center/guidance/can-spam-act-compliance-guide-business" target="_blank">here</a> for more details.</p>`,
                    actions: actions,
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        getContact() {
            this.loading = true;
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Contacts?$filter=Id eq ${this.contact.id}&$expand=SalesPerson,ContactStatus,Company,ListContacts($expand=List) `,
                    )
                    .then((response) => {
                        this.contact.clientId = response.data.value[0].ClientId;
                        this.contact.profileImageUrl = response.data.value[0].ProfileImageUrl;
                        this.contact.initialLeadScore = response.data.value[0].InitialLeadScore;
                        this.contact.buyerRole = this.getStatusById(
                            response.data.value[0].BuyerRoleId,
                        );
                        this.contact.linkedinUrl = response.data.value[0].LinkedinUrl;
                        this.contact.title = response.data.value[0].Title;
                        this.contact.facebookUrl = response.data.value[0].FacebookUrl;
                        this.contact.twitterUrl = response.data.value[0].TwitterUrl;
                        this.contact.firstName = response.data.value[0].FirstName;
                        this.contact.lastName = response.data.value[0].LastName;
                        this.contact.city = response.data.value[0].City;
                        this.contact.region = response.data.value[0].Region;
                        this.contact.phone = response.data.value[0].Phone;
                        this.contact.mobilePhone = response.data.value[0].MobilePhone;
                        this.contact.teamsPhone = response.data.value[0].TeamsPhone;
                        this.contact.homePhone = response.data.value[0].HomePhone;
                        this.contact.addressLine1 = response.data.value[0].Address;
                        this.contact.addressLine2 = response.data.value[0].Address2;
                        this.contact.zipCode = response.data.value[0].ZipCode;

                        this.contact.isSubscribed =
                            response.data.value[0].IsSubscribed ||
                            response.data.value[0].IsSubscribed === null
                                ? this.subscribeOptions[0]
                                : this.subscribeOptions[1];

                        this.contact.isBounced = response.data.value[0].IsBounced
                            ? this.bouncedOptions[0]
                            : this.bouncedOptions[1];
                        this.contact.initialIsSubscribed = this.contact.isSubscribed;
                        this.contact.email = response.data.value[0].Email;
                        this.contact.initialEmail = this.contact.email;

                        if (response.data.value[0].ContactStatus) {
                            this.contact.contactStatus = {
                                id: response.data.value[0].ContactStatus.Id,
                                name: response.data.value[0].ContactStatus.Name,
                                color: response.data.value[0].ContactStatus.Color,
                                backgroundClass: this.$helpers.getStatusBackgroundClass(
                                    response.data.value[0].ContactStatus.Color,
                                ),
                            };
                        }

                        if (response.data.value[0].Company) {
                            this.contact.company = {
                                id: response.data.value[0].Company.Id,
                                name: response.data.value[0].Company.Name,
                                imageUrl: response.data.value[0].Company.LogoUrl,
                            };
                        }

                        if (response.data.value[0].SalesPerson) {
                            this.contact.salesperson = {
                                id: response.data.value[0].SalesPerson.Id,
                                profileImageUrl:
                                    response.data.value[0].SalesPerson.ProfileImageUrl ||
                                    require('@/assets/images/avatar.png'),
                                name: this.$helpers.getName(
                                    response.data.value[0].SalesPerson.FirstName,
                                    response.data.value[0].SalesPerson.LastName,
                                ),
                                title: response.data.value[0].SalesPerson.Title,
                            };
                        }

                        if (response.data.value[0].ListContacts) {
                            var colorClasses = [
                                'solid-red-bg',
                                'solid-orange-bg',
                                'solid-blue-bg',
                                'solid-green-bg',
                                'solid-grey-bg',
                            ];
                            var listContacts = response.data.value[0].ListContacts;

                            for (var i = 0; i < listContacts.length; i++) {
                                this.contact.lists.push({
                                    id: listContacts[i].List.Id,
                                    name: listContacts[i].List.Name,
                                    class: colorClasses[i % 5],
                                });
                            }
                        }

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get your contact data', {
                            error,
                        });
                        reject();
                    });
            });
        },

        saveContact() {
            this.currentStep = this.steps['saving'];
            const trackingId = uuidv4();

            if (this.contact.email && this.contact.email !== this.contact.initialEmail) {
                this.checkIfEmailExists()
                    .then(this.patchContact(trackingId))
                    .then(() => {
                        this.saveCustomFields(this.customContactFields);
                    })
                    .then(() => {
                        this.$helpers.showSuccessNotification('Your changes have been saved.');
                        EventBus.$emit('contact:update');

                        this.closeWidget();

                        if (
                            this.contact.initialIsSubscribed === 'unsubscribed' &&
                            this.contact.isSubscribed === this.subscribeOptions[0]
                        ) {
                            this.showSubscriptionWarning();
                        }
                    })
                    .catch((error) => {
                        this.goToContactStep();
                    });
            } else {
                this.patchContact(trackingId)
                    .then(this.updateLists(trackingId))
                    .then(this.saveCustomFields(trackingId))
                    .then(() => {
                        this.$helpers.showSuccessNotification(
                            `Your changes to ${this.contact.firstName} have been saved.`,
                        );
                        EventBus.$emit('contact:update');

                        this.closeWidget();

                        if (
                            this.contact.initialIsSubscribed === 'unsubscribed' &&
                            this.contact.isSubscribed === this.subscribeOptions[0]
                        ) {
                            this.showSubscriptionWarning();
                        }
                    })
                    .catch((response) => {
                        this.goToContactStep();
                    });
            }
        },

        updateLists(trackingId) {
            return new Promise((resolve, reject) => {
                var request = this.contact.lists.map((list) => list.id);
                this.$api
                    .put(`/api/Contacts/${this.contact.id}/Lists?trackingId=${trackingId}`, request)
                    .then(() => {
                        resolve();
                    })
                    .catch(reject);
            });
        },

        patchContact(trackingId) {
            var changes = [
                { name: 'ProfileImageUrl', value: this.contact.profileImageUrl },
                { name: 'InitialLeadScore', value: this.contact.initialLeadScore },
                {
                    name: 'IsSubscribed',
                    value: this.contact.isSubscribed === this.subscribeOptions[0],
                },
                { name: 'BuyerRoleId', value: this.contact.buyerRole.value || null },
                { name: 'FirstName', value: this.contact.firstName },
                { name: 'LastName', value: this.contact.lastName },
                { name: 'Title', value: this.contact.title },
                { name: 'Email', value: this.contact.email },
                { name: 'Phone', value: this.contact.phone },
                { name: 'MobilePhone', value: this.contact.mobilePhone },
                { name: 'TeamsPhone', value: this.contact.teamsPhone },
                { name: 'HomePhone', value: this.contact.homePhone },
                { name: 'City', value: this.contact.city },
                { name: 'Region', value: this.contact.region },
                { name: 'Address', value: this.contact.addressLine1 },
                { name: 'Address2', value: this.contact.addressLine2 },
                { name: 'ZipCode', value: this.contact.zipCode },
                { name: 'LinkedinUrl', value: this.contact.linkedinUrl },
                { name: 'FacebookUrl', value: this.contact.facebookUrl },
                { name: 'TwitterUrl', value: this.contact.twitterUrl },
                {
                    name: 'ContactStatusId',
                    value: this.contact.contactStatus ? this.contact.contactStatus.id : null,
                },
                { name: 'CompanyId', value: this.contact.company ? this.contact.company.id : null },
                {
                    name: 'SalesPersonId',
                    value: this.contact.salesperson ? this.contact.salesperson.id : null,
                },
                {
                    name: 'IsBounced',
                    value: this.contact.isBounced === this.bouncedOptions[0],
                },
            ];

            return new Promise((resolve, reject) => {
                this.$api
                    .patch(`/api/Contacts/${this.contact.id}?trackingId=${trackingId}`, changes)
                    .then(() => {
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to save your contact.', {
                            error,
                        });
                        reject();
                    });
            });
        },

        checkIfEmailExists() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Contacts?$select=Id&$filter=ClientId eq ${this.contact.clientId} and Email eq '${this.contact.email}'`,
                    )
                    .then((response) => {
                        if (response.data.value.length > 0) {
                            const contactId = response.data.value[0].Id;
                            this.$helpers.showFailNotification(
                                `A contact already has this email: <a href="/Contacts/${contactId}">Click to see contact</a>`,
                                { level: LogLevel.DEBUG },
                            );
                            log.error(
                                `Unable to change a contact's email to match another.`,
                                response,
                            );
                            reject();
                        } else {
                            resolve();
                        }
                    });
            });
        },

        getStatusById(buyerRole) {
            switch (buyerRole) {
                case null:
                    return this.buyerRoleOptions[2];
                    break;
                case 0:
                    return this.buyerRoleOptions[2];
                    break;
                case 1:
                    return this.buyerRoleOptions[1];
                    break;
                case 2:
                    return this.buyerRoleOptions[0];
                    break;
            }
        },

        getCustomContactFields(options) {
            return new Promise((resolve, reject) => {
                this.getCustomFields(options, this.customFieldsConfig)
                    .then(({ items }) => {
                        return this.getCustomFieldValues(items);
                    })
                    .then(resolve)
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your custom fields data.',
                            { error },
                        );
                    });
            });
        },

        getCustomFieldValues(items) {
            return new Promise((resolve, reject) => {
                const valuesRequests = items.map((item) => {
                    return this.getCustomFieldValue(this.customFieldsConfig, item);
                });

                Promise.all(valuesRequests)
                    .then((values) => {
                        this.customContactFields = values;
                        resolve();
                    })
                    .catch(reject);
            });
        },

        saveCustomFields(trackingId) {
            const service = '/ms/custom-fields/v1/customContactFieldValues';
            return new Promise((resolve, reject) => {
                const valueRequests = this.customContactFields.map((item) => {
                    const customFieldValue = {
                        ContactId: this.contact.id,
                        CustomContactFieldId: item.id,
                        [item.customFieldValue.name]: this.customFieldValue(
                            item.customFieldValue.value,
                            item.inputType,
                        ),
                    };

                    if (item.customFieldValueId) {
                        return this.$api.put(
                            `${service}/${item.customFieldValueId}/${this.contact.clientId}?trackingId=${trackingId}`,
                            customFieldValue,
                        );
                    }

                    return this.$api.post(`${service}/${this.contact.clientId}`, customFieldValue);
                });

                Promise.all(valueRequests)
                    .then(resolve)
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'Failed to save your contact custom fields.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        customFieldValue(value, type) {
            if (type.includes('checkbox') || type.includes('dropdown')) {
                return JSON.stringify(value);
            } else if (type === 'number') {
                return +value;
            }
            return value;
        },

        updateImageUrl(imageUrl) {
            this.contact.profileImageUrl = imageUrl;
        },

        closeWidget() {
            EventBus.$emit('close-widget');
        },

        goToStep(step) {
            this.currentStep = this.steps[step];
        },

        goToContactStep() {
            this.currentStep = this.steps['contact'];
        },

        goToContact() {
            this.closeWidget();
            this.$router.push(`/Contacts/${this.contact.id}`);
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        openCustomFieldMenu() {
            EventBus.$emit('open-widget', {
                name: 'custom-field-widget',
                options: {
                    type: 'Contact',
                    clientId: this.contact.clientId,
                },
            });
        },
    },
};
</script>

<style scoped>
.contact-edit-widget .step {
    width: 100%;
}

.contact-edit-widget .content-wrapper .input-container.zip-code-container {
    max-width: 266px;
}

.contact-edit-widget .content-wrapper .input-container label {
    color: #382153;
    font: 500 16px/1em 'Roboto';
}

/* .contact-edit-widget .content-wrapper .input-container .app-input {
    color: #382153;
    font-size: 16px;
    font-family: roboto;
    font-weight: 500;
} */

.contact-edit-widget .step .contact-form-row {
    display: flex;
    justify-content: space-between;
    margin-left: -10px;
    margin-right: -10px;
}

.contact-edit-widget .step .bordered-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    margin-bottom: 2.5em;
    padding: 32px;
    position: relative;
}

.contact-edit-widget .step .bordered-container:last-child {
    margin-bottom: 0;
}

.contact-edit-widget .step .bordered-container .title {
    background-color: #ffffff;
    font-weight: 600;
    left: 50%;
    margin-left: -82px;
    padding: 0px 25px;
    position: absolute;
    top: -11px;
    color: #382153;
    font-size: 18px;
    font-family: 'Roboto';
}

.contact-edit-widget .step .bordered-container .title.long-title {
    margin-left: -140px;
    padding: 0px 56px;
}

.contact-edit-widget .step .contact-form-row.contact-profile-image {
    align-items: center;
    margin: 0 auto 25px auto;
}

.contact-edit-widget .step .contact-form-row.contact-profile-image .file-input {
    display: none;
}

.contact-edit-widget .step .contact-form-row.contact-profile-image .profile-image {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    margin-right: 35px;
}

.contact-edit-widget .step .contact-form-row .input-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;
    flex-grow: 1;
    margin-left: 10px;
    margin-right: 10px;
}

.contact-edit-widget .step .contact-form-row .description-container {
    width: 100%;
}

.contact-edit-widget .step .contact-form-row .input-container textarea {
    border: 1px solid #e9e9f0;
    width: 100%;
    max-width: 100%;
    min-height: 150px;
}

.contact-edit-widget .action-container {
    display: flex;
}

.contact-edit-widget .action-container .btn {
    margin-left: 15px;
}

.contact-edit-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.contact-edit-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.contact-edit-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18aeff;
}

.contact-edit-widget .action-container .btn.transparent-btn:hover {
    background: #dbedfa;
}

.contact-edit-widget .lists {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -7px;
}

.contact-edit-widget .lists .list {
    color: #ffffff;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 15px;
    margin: 0 7px 9px 7px;
    display: flex;
    align-items: center;
}

.contact-edit-widget .input-container {
    width: 100%;
}
</style>

<style>
.contact-edit-widget .lists .list svg {
    margin-left: 12px;
    width: 7px;
    cursor: pointer;
}

.contact-edit-widget .lists .list svg path {
    fill: #ffffff;
}

.contact-edit-widget .list-search {
    margin-bottom: 10px;
}

.contact-edit-widget .add {
    width: 100%;
}

.contact-edit-widget .add svg {
    margin-left: 16px;
}

.contact-edit-widget .add svg path {
    fill: #ffffff;
}

.contact-edit-widget .app-border-section .input-container {
    width: 100%;
}

.contact-edit-widget .disclaimer {
    font-size: 12px;
    padding: 4px 0px;
}

.contact-edit-widget .disclaimer a {
    color: #18aeff;
}

.contact-edit-widget .widget-contents-container .loading-screen {
    margin-left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.contact-edit-widget .loading-card,
.contact-edit-widget .loading-screen {
    height: 100%;
}
</style>
